import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable } from 'rxjs';
import { LogsService } from '../logs/logs.service';
import { TenantService } from '../tenant/tenant.service';
import { IUser } from '../user/models/user.model';
import { UserService } from '../user/user.service';

export const SECURE_PRINT_LEVELS = {
  QR: 'QR',
  NFC: 'NFC',
  ON_DEVICE_RELEASE: 'ON_DEVICE_RELEASE'
};

@Injectable({
  providedIn: 'root'
})

export class SecurePrintService {

  public tenant = this.tenantService.tenantSignal;
  private logger: Logger;

  constructor(
    public http: HttpClient,
    private logsService: LogsService,
    private tenantService: TenantService,
    private userService: UserService,
    loggingService: LoggingService,
  ) {
    this.logger = loggingService.getLogger("[SecurePrintService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public getUserSecurityLevel(): Observable<string | null> {
    this.logger.info('getUserSecurityLevel()');
    let securePrintLevel: string | null = null;
    const user: IUser | null = this.userService.userSignal();

    return new Observable((observer) => {
      const tenant = this.tenant();
      const securePrintConfiguration: any = tenant ? tenant.embedded.securePrintConfiguration : null;
      if (securePrintConfiguration) {
        this.logsService.logSecuritySettings(securePrintConfiguration);

        const securityLevelGroupsQR = securePrintConfiguration._embedded.securityLevelQR;
        const securityLevelGroupsNFC = securePrintConfiguration._embedded.securityLevelNFC;
        const securityLevelGroupsON_DEVICE_RELEASE = securePrintConfiguration._embedded.securityLevelON_DEVICE_RELEASE;
        const mustPrintSecurelyOndeviceOnly = securePrintConfiguration.mustPrintSecurelyOndeviceOnly; // Users who must print securely, will only need to do this for printers with Printix Go.(text from printix administrator)


        if (securityLevelGroupsQR.length > 0) {
          securePrintLevel = this.setSecurePrintLevelFromGroup(user, securePrintLevel, securityLevelGroupsQR, 'QR');
        }
        if (securityLevelGroupsNFC.length > 0) {
          securePrintLevel = this.setSecurePrintLevelFromGroup(user, securePrintLevel, securityLevelGroupsNFC, 'NFC');
        }
        if (securityLevelGroupsON_DEVICE_RELEASE.length > 0) {
          securePrintLevel = this.setSecurePrintLevelFromGroup(user, securePrintLevel, securityLevelGroupsON_DEVICE_RELEASE, 'ON_DEVICE_RELEASE');
        }
        if (!securePrintLevel && securePrintConfiguration.minimumSecurityLevel !== 'NONE') {
          securePrintLevel = securePrintConfiguration.minimumSecurityLevel;
        }
        if (securePrintLevel !== null && mustPrintSecurelyOndeviceOnly) {
          securePrintLevel = 'ON_DEVICE_RELEASE';
        }
      }
      observer.next(securePrintLevel);
      observer.complete();
    });
  }

  private setSecurePrintLevelFromGroup(
    user: IUser | null,
    securityPrintLevel: string | null,
    securityLevelGroups: any[],
    securityLevel: string
  ): string | null {
    if (securityLevelGroups.length > 0 && user && user.groups && user.groups.length > 0) {
      const userGroups = user.groups;
      securityLevelGroups.forEach((securityLevelGroup: any) => {
        userGroups.forEach((userGroupID: string) => {
          if (userGroupID === securityLevelGroup._links.self.href) {
            securityPrintLevel = securityLevel;
          }
        });
      });
    }
    return securityPrintLevel;
  }

  public setSecurePrintMethods(user: IUser | null): Observable<Array<string>> {
    this.logger.info('setSecurePrintMethods()');
    const securePrintMethods: Array<string> | null = user?.userAppSettings?.securePrintMethods ?? null;
    return new Observable((observer) => {
      if (!securePrintMethods) {
        const defaultMethods = [SECURE_PRINT_LEVELS.QR, SECURE_PRINT_LEVELS.NFC];
        this.userService.setSecurePrintMethods(defaultMethods).subscribe({
          next: () => {
            observer.next(defaultMethods);
            observer.complete();
          },
          // Without this error branch the outer Observable never settles when the
          // PATCH fails (e.g. stale lastUsedTenant after upgrade points at the
          // lighter px:tenant-user endpoint), which stalls initiatePage() and
          // leaves PrintJobsPage blank because dataIsLoaded() never flips true.
          error: (error: any) => observer.error(error)
        });
      } else {
        const onDeviceReleaseIndex: number = securePrintMethods.indexOf('ON_DEVICE_RELEASE');
        if (onDeviceReleaseIndex !== -1) {
          securePrintMethods.splice(onDeviceReleaseIndex, 1);
        }
        observer.next(securePrintMethods);
        observer.complete();

      }
    });
  }
}